import React, { useState } from "react";
import { TEMPLATE_COLUMNS, TEMPLATE_SORT_KEYS } from "./reportColumnCatalog";

const FILTER_LABELS = {
  status: "Status",
  leadType: "Lead Type",
  leadSource: "Lead Source",
  serviceDashboard: "Dashboard",
  salesRep: "Sales Rep",
  category: "Category",
  nextContact: "Next Contact",
  search: "Search",
};

function fmtFilterValue(v) {
  if (v === null || v === undefined || v === "") return "";
  if (Array.isArray(v)) return v.join(", ");
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function activeFilterEntries(filters = {}) {
  const out = [];
  for (const [k, v] of Object.entries(filters)) {
    if (["dateRange", "dateField", "dateFrom", "dateTo"].includes(k)) {
      if (k === "dateRange" && v && v !== "all") out.push(["Range", v]);
      continue;
    }
    if (v === undefined || v === null || v === "" || v === "all") continue;
    if (Array.isArray(v) && v.length === 0) continue;
    out.push([FILTER_LABELS[k] || k, fmtFilterValue(v)]);
  }
  return out;
}

function columnListFromPayload(payload) {
  const vc = payload?.visibleColumns;
  const order = Array.isArray(payload?.columnOrder) ? payload.columnOrder : [];
  const labels = payload?.columnLabels || {};

  if (!vc || typeof vc !== "object") return [];

  const fromOrder = order.filter((k) => vc[k]);
  const extras = Object.keys(vc).filter(
    (k) => vc[k] && !fromOrder.includes(k),
  );
  const keys = [...fromOrder, ...extras];

  return keys
    .map((vk) => {
      const def = TEMPLATE_COLUMNS.find((c) => c.viewKey === vk);
      if (!def) return null;
      return labels[vk] || def.label;
    })
    .filter(Boolean);
}

function sortSummary(sortState = []) {
  if (!Array.isArray(sortState) || sortState.length === 0) return [];
  return sortState.map((s, i) => {
    const key = TEMPLATE_SORT_KEYS.find((k) => k.key === s.col);
    const dir = s.dir === "desc" ? "▼" : "▲";
    return `${key?.label || s.col} ${dir}${i > 0 ? ` (${i + 1})` : ""}`;
  });
}

export default function ReportViewSummaryCard({ view, preset, title }) {
  const [open, setOpen] = useState(true);

  if (preset) {
    const columnLabels = (preset.columns || [])
      .map((vk) => TEMPLATE_COLUMNS.find((c) => c.viewKey === vk)?.label)
      .filter(Boolean);

    return (
      <div className="r6-tpl-summary">
        <button
          type="button"
          className="r6-tpl-summary-toggle"
          onClick={() => setOpen((v) => !v)}
        >
          <i className={`fas fa-chevron-${open ? "down" : "right"}`} />
          <span className="r6-tpl-summary-kind is-view">Preset</span>
          <span className="r6-tpl-summary-title">{preset.label}</span>
          <span className="r6-tpl-summary-count">
            {columnLabels.length} cols
          </span>
        </button>
        {open && (
          <div className="r6-tpl-summary-body">
            <div className="r6-tpl-summary-row">
              <div className="r6-tpl-summary-label">Columns</div>
              <div className="r6-tpl-summary-chips">
                {columnLabels.map((lbl, i) => (
                  <span key={i} className="r6-tpl-chip">{lbl}</span>
                ))}
              </div>
            </div>
            <div className="r6-tpl-summary-hint">
              Built-in preset — applies column layout only.
            </div>
          </div>
        )}
      </div>
    );
  }

  if (!view) return null;

  const payload = view.payload || {};
  const isTemplate = !!view.is_report_template;
  const kind = isTemplate ? "Report Template" : "Saved View";
  const name = title || view.name || "(unnamed)";

  const columns = columnListFromPayload(payload);
  const activeChips = Array.isArray(payload.activeQuickChips)
    ? payload.activeQuickChips
    : [];
  const filterEntries = activeFilterEntries(payload.filters || {});
  const sorts = sortSummary(payload.sortState);

  const totalAttrs =
    columns.length + activeChips.length + filterEntries.length + sorts.length;

  return (
    <div className="r6-tpl-summary">
      <button
        type="button"
        className="r6-tpl-summary-toggle"
        onClick={() => setOpen((v) => !v)}
      >
        <i className={`fas fa-chevron-${open ? "down" : "right"}`} />
        <span
          className={`r6-tpl-summary-kind ${
            isTemplate ? "is-template" : "is-view"
          }`}
        >
          {kind}
        </span>
        <span className="r6-tpl-summary-title">{name}</span>
        <span className="r6-tpl-summary-count">{totalAttrs} settings</span>
      </button>

      {open && (
        <div className="r6-tpl-summary-body">
          {isTemplate && (payload.title || payload.headerText) && (
            <div className="r6-tpl-summary-row">
              <div className="r6-tpl-summary-label">Header</div>
              <div className="r6-tpl-summary-values">
                {payload.title && (
                  <div className="r6-tpl-summary-title-line">
                    {payload.title}
                  </div>
                )}
                {payload.headerText && (
                  <div className="r6-tpl-summary-sub-line">
                    {payload.headerText}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="r6-tpl-summary-row">
            <div className="r6-tpl-summary-label">Columns</div>
            <div className="r6-tpl-summary-chips">
              {columns.length === 0 ? (
                <span className="r6-tpl-summary-muted">Default columns</span>
              ) : (
                columns.map((lbl, i) => (
                  <span key={i} className="r6-tpl-chip">{lbl}</span>
                ))
              )}
            </div>
          </div>

          {activeChips.length > 0 && (
            <div className="r6-tpl-summary-row">
              <div className="r6-tpl-summary-label">Quick filters</div>
              <div className="r6-tpl-summary-chips">
                {activeChips.map((k) => (
                  <span key={k} className="r6-tpl-chip r6-tpl-chip-quick">
                    {k.replace(/_/g, " ")}
                  </span>
                ))}
              </div>
            </div>
          )}

          {filterEntries.length > 0 && (
            <div className="r6-tpl-summary-row">
              <div className="r6-tpl-summary-label">Filters</div>
              <div className="r6-tpl-summary-values">
                {filterEntries.map(([k, v]) => (
                  <div key={k} className="r6-tpl-summary-line">
                    <span className="r6-tpl-summary-kv-key">{k}:</span>
                    <span className="r6-tpl-summary-kv-val">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {sorts.length > 0 && (
            <div className="r6-tpl-summary-row">
              <div className="r6-tpl-summary-label">Sort</div>
              <div className="r6-tpl-summary-chips">
                {sorts.map((s, i) => (
                  <span key={i} className="r6-tpl-chip r6-tpl-chip-sort">
                    {s}
                  </span>
                ))}
              </div>
            </div>
          )}

          {totalAttrs === 0 && (
            <div className="r6-tpl-summary-hint">
              Uses defaults — no explicit columns, filters, or sort stored.
            </div>
          )}
        </div>
      )}
    </div>
  );
}